import { useEffect, useState } from "react";
import { FRACTION_KEYS, keypadFrom, keypadPress, keypadText, keypadValue, stepValue, type KeypadKey, type KeypadState } from "../../../shared/kitchen/keypad";
import {
  convert,
  formatAmount,
  INGREDIENT_KEYS,
  INGREDIENTS,
  nearestOvenMark,
  OVEN_MARKS,
  stepFor,
  TEMP_UNITS,
  UNIT_LABEL,
  unitLabel,
  VOLUME_UNITS,
  WEIGHT_UNITS,
  type IngredientKey,
  type TempUnit,
  type Unit,
} from "../../../shared/kitchen/units";
import { readPref, writePref } from "../../data/persist";
import type { WidgetDefinition, WidgetProps } from "../types";
import { WidgetHeader } from "../WidgetHeader";
import { KGlyph } from "./glyphs";
import "./kitchen.css";

type Mode = "volume" | "weight" | "temp" | "ingredient";

interface ConverterConfig {
  mode: Mode;
}

const MODES: { id: Mode; label: string; short: string }[] = [
  { id: "volume", label: "Volume", short: "Vol" },
  { id: "weight", label: "Weight", short: "Wt" },
  { id: "temp", label: "Temp", short: "°" },
  { id: "ingredient", label: "Cups ↔ grams", short: "Cup↔g" },
];

const FROM_UNITS: Record<Mode, readonly Unit[]> = {
  volume: VOLUME_UNITS,
  weight: WEIGHT_UNITS,
  temp: TEMP_UNITS,
  ingredient: ["cup", "tbsp", "tsp", "g", "oz"],
};
const TO_UNITS: Record<Mode, readonly Unit[]> = {
  volume: VOLUME_UNITS,
  weight: WEIGHT_UNITS,
  temp: TEMP_UNITS,
  ingredient: ["g", "oz", "cup", "tbsp", "tsp"],
};
const DEFAULTS: Record<Mode, { from: Unit; to: Unit; value: number }> = {
  volume: { from: "cup", to: "ml", value: 1 },
  weight: { from: "oz", to: "g", value: 8 },
  temp: { from: "F", to: "C", value: 350 },
  ingredient: { from: "cup", to: "g", value: 1 },
};

interface Saved {
  mode: Mode;
  units: Partial<Record<Mode, { from: Unit; to: Unit }>>;
  ingredient: IngredientKey;
}

function Converter({ instanceId, config, size, editing, fullscreen, openFullscreen }: WidgetProps<ConverterConfig>) {
  const prefKey = `kitchen.converter.${instanceId}`;
  const [saved, setSaved] = useState<Saved>(() => {
    const s = readPref<Partial<Saved>>(prefKey, {});
    return { mode: s.mode && FROM_UNITS[s.mode] ? s.mode : config.mode, units: s.units ?? {}, ingredient: s.ingredient && INGREDIENTS[s.ingredient] ? s.ingredient : "flour" };
  });
  useEffect(() => writePref(prefKey, saved), [prefKey, saved]);

  const mode = saved.mode;
  const units = saved.units[mode] ?? DEFAULTS[mode];
  const from = FROM_UNITS[mode].includes(units.from) ? units.from : DEFAULTS[mode].from;
  const to = TO_UNITS[mode].includes(units.to) ? units.to : DEFAULTS[mode].to;
  const [pad, setPad] = useState<KeypadState>(() => keypadFrom(DEFAULTS[mode].value));
  const value = keypadValue(pad);
  const ingredient = saved.ingredient;

  const setUnits = (next: { from: Unit; to: Unit }) => setSaved((s) => ({ ...s, units: { ...s.units, [mode]: next } }));
  const setMode = (m: Mode) => {
    setSaved((s) => ({ ...s, mode: m }));
    setPad(keypadFrom(DEFAULTS[m].value));
  };
  const swap = () => {
    // In cups↔grams the two sides have different unit sets; only swap when both fit.
    const nextFrom = FROM_UNITS[mode].includes(to) ? to : from;
    const nextTo = TO_UNITS[mode].includes(from) ? from : to;
    const result = convert(value, from, to, ingredient);
    setUnits({ from: nextFrom, to: nextTo });
    if (result !== null && nextFrom === to) setPad(keypadFrom(Number(result.toFixed(mode === "temp" ? 0 : 2))));
  };
  const press = (k: KeypadKey) => setPad((p) => keypadPress(p, k));
  const step = (dir: 1 | -1) => setPad(keypadFrom(stepValue(value, stepFor(from), dir, mode === "temp" ? -100 : 0)));

  const result = convert(value, from, to, ingredient);
  const out = result === null ? { text: "–", approx: false } : formatAmount(result, to);
  const also = TO_UNITS[mode]
    .filter((u) => u !== to && u !== from)
    .map((u) => ({ u, v: convert(value, from, u, ingredient) }))
    .filter((x): x is { u: Unit; v: number } => x.v !== null);
  const mark = mode === "temp" ? nearestOvenMark(value, from as TempUnit) : null;
  const compact = size.variant === "compact" && !fullscreen;
  const fractions = mode === "volume" || mode === "ingredient";

  // Calculator-style pad; cooking fractions get their own row in volume modes.
  const keys: { k: KeypadKey; span?: number }[] = [
    { k: "7" }, { k: "8" }, { k: "9" }, { k: "back" },
    { k: "4" }, { k: "5" }, { k: "6" }, { k: "clear" },
    { k: "1" }, { k: "2" }, { k: "3" }, { k: mode === "temp" ? "sign" : "." },
    ...(mode === "temp" ? [{ k: "0" as const, span: 3 }, { k: "." as const }] : [{ k: "0" as const, span: 4 }]),
  ];
  const keyLabel = (k: KeypadKey) => (k === "sign" ? "±" : k === "clear" ? "C" : (FRACTION_KEYS.find((f) => f.key === k)?.label ?? k));
  const keyName = (k: KeypadKey) => (k === "back" ? "Delete" : k === "clear" ? "Clear" : k === "sign" ? "Plus or minus" : k === "." ? "Decimal point" : keyLabel(k));

  return (
    <div className={`kc mode-${mode} ${compact ? "is-compact" : ""}`}>
      <WidgetHeader title="Converter" onTitle={fullscreen ? undefined : openFullscreen} />
      <div className="segmented kc-modes" role="tablist" aria-label="Conversion type">
        {MODES.map((m) => (
          <button key={m.id} role="tab" aria-selected={mode === m.id} className={mode === m.id ? "on" : ""} onClick={() => setMode(m.id)} disabled={editing}>
            <span className="kc-mode-long">{m.label}</span>
            <span className="kc-mode-short" aria-hidden="true">
              {m.short}
            </span>
          </button>
        ))}
      </div>

      <div className="kc-body">
        <section className="kc-in" aria-label="Amount">
          <div className="kc-display">
            <button className="kk-step" onClick={() => step(-1)} aria-label="Decrease" disabled={editing}>
              <KGlyph name="minus" />
            </button>
            <output className="kc-value" aria-live="polite">
              <span className="kc-num">{keypadText(pad)}</span>
              <span className="kc-unit">{unitLabel(from, value)}</span>
            </output>
            <button className="kk-step" onClick={() => step(1)} aria-label="Increase" disabled={editing}>
              <svg width="1.35rem" height="1.35rem" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" aria-hidden="true">
                <path d="M12 5v14M5 12h14" />
              </svg>
            </button>
          </div>
          <div className="chip-row kc-units" role="radiogroup" aria-label="From">
            {FROM_UNITS[mode].map((u) => (
              <button key={u} role="radio" aria-checked={from === u} className={`chip chip-sm ${from === u ? "on" : ""}`} onClick={() => setUnits({ from: u, to: u === to ? from : to })} disabled={editing}>
                {UNIT_LABEL[u].short}
              </button>
            ))}
          </div>
          {fractions && (
            <div className="kc-fracs" role="group" aria-label="Fractions">
              {FRACTION_KEYS.map((f) => (
                <button key={f.key} className={`kk-key kk-alt ${pad.frac === f.key ? "on" : ""}`} onClick={() => press(f.key)} aria-label={f.label} aria-pressed={pad.frac === f.key} disabled={editing || pad.digits.includes(".")}>
                  {f.label}
                </button>
              ))}
            </div>
          )}
          <div className="kc-pad">
            {keys.map(({ k, span }) => (
              <button
                key={k}
                className={`kk-key ${k === "back" || k === "clear" || k === "sign" ? "kk-alt" : ""}`}
                style={span ? { gridColumn: `span ${span}` } : undefined}
                onClick={() => press(k)}
                aria-label={keyName(k)}
                disabled={editing}
              >
                {k === "back" ? <KGlyph name="backspace" /> : keyLabel(k)}
              </button>
            ))}
          </div>
        </section>

        <section className="kc-out" aria-label="Result">
          <div className="kc-to">
            <button className="icon-btn kc-swap" onClick={swap} aria-label="Swap units" disabled={editing}>
              <KGlyph name="swap" />
            </button>
            <div className="chip-row kc-units" role="radiogroup" aria-label="To">
              {TO_UNITS[mode].map((u) => (
                <button key={u} role="radio" aria-checked={to === u} className={`chip chip-sm ${to === u ? "on" : ""}`} onClick={() => setUnits({ from: u === from ? to : from, to: u })} disabled={editing}>
                  {UNIT_LABEL[u].short}
                </button>
              ))}
            </div>
          </div>
          <div className="kc-result" aria-live="polite">
            <span className="kc-approx">{out.approx || mode === "ingredient" ? "≈" : "="}</span>
            <strong className="kc-big">{out.text}</strong>
            <span className="kc-big-unit">{result === null ? "" : unitLabel(to, result)}</span>
          </div>

          {mode === "ingredient" && (
            <>
              <div className="chip-row wrap kc-ingredients" role="radiogroup" aria-label="Ingredient">
                {INGREDIENT_KEYS.map((k) => (
                  <button key={k} role="radio" aria-checked={ingredient === k} className={`chip chip-sm ${ingredient === k ? "on" : ""}`} onClick={() => setSaved((s) => ({ ...s, ingredient: k }))} disabled={editing}>
                    {INGREDIENTS[k].label}
                  </button>
                ))}
              </div>
              <p className="kc-note">
                Approximate: 1 cup {INGREDIENTS[ingredient].label.toLowerCase()} ≈ {INGREDIENTS[ingredient].gramsPerCup} g
                {INGREDIENTS[ingredient].note ? ` (${INGREDIENTS[ingredient].note})` : ""}. Weigh for baking precision.
              </p>
            </>
          )}

          {mode === "temp" && (
            <>
              {mark && (
                <p className="kc-mark">
                  <span className="pill static">
                    Gas mark <b>{mark.gas}</b>
                  </span>
                  <span className="muted small">{mark.label} oven</span>
                </p>
              )}
              {!compact && (
                <div className="chip-row wrap kc-marks" aria-label="Oven settings">
                  {OVEN_MARKS.map((m) => (
                    <button key={m.gas} className={`chip chip-sm ${mark?.gas === m.gas ? "on" : ""}`} onClick={() => setPad(keypadFrom(from === "F" ? m.f : m.c))} disabled={editing}>
                      {from === "F" ? m.f : m.c}°
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {mode !== "temp" && !compact && also.length > 0 && (
            <ul className="kc-also">
              {also.slice(0, 4).map(({ u, v }) => {
                const f = formatAmount(v, u);
                return (
                  <li key={u}>
                    <button onClick={() => setUnits({ from, to: u })} disabled={editing}>
                      <span>{f.approx ? "≈ " : ""}{f.text}</span>
                      <span className="kc-also-unit">{unitLabel(u, v)}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {mode === "volume" && <p className="kc-note">US cups and spoons. 1 cup = 16 tbsp = 48 tsp.</p>}
        </section>
      </div>
    </div>
  );
}

export const converterWidget: WidgetDefinition<ConverterConfig> = {
  type: "converter",
  version: 1,
  name: "Kitchen converter",
  description: "Cups, spoons, grams, ounces, oven temperatures. Tap to convert, no typing.",
  icon: "sync",
  category: "household",
  defaultSize: { w: 5, h: 6 },
  minSize: { w: 3, h: 4 },
  stackHeight: 640,
  defaults: { mode: "volume" },
  settings: [
    {
      key: "mode",
      label: "Start on",
      type: "select",
      options: [
        { value: "volume", label: "Volume" },
        { value: "weight", label: "Weight" },
        { value: "temp", label: "Temperature" },
        { value: "ingredient", label: "Cups ↔ grams" },
      ],
    },
  ],
  provider: "local",
  supportsFullscreen: true,
  Component: Converter,
};
